import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';

/** GET api/feature-allow-list/me: the signed-in person's UI feature keys (pricingdb.feature_allow_list). */
export interface MyFeatureKeys {
  email: string | null;
  featureKeys: string[];
}

// Which UI features the signed-in person sees, from the workflow API's allow-list. A show/hide convenience, not access
// control: the API doesn't enforce it. Loaded once per session on first use; until it loads, and if it fails, every
// feature reads as off, so gated UI stays hidden rather than flashing in.
@Injectable({ providedIn: 'root' })
export class FeatureAccessService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.api.cmaiContractsPricingWorkflowApi}feature-allow-list/me`;

  private readonly keys = signal<ReadonlySet<string>>(new Set());
  private readonly loadedState = signal(false);
  private requested = false;
  private resolveReady!: () => void;

  /** Settles once the allow-list has answered (or failed); for route guards that must wait for it. */
  readonly ready: Promise<void> = new Promise<void>(resolve => { this.resolveReady = resolve; });

  /** True once the allow-list has answered (or failed). */
  readonly loaded = this.loadedState.asReadonly();
  /** The person's feature keys, sorted. */
  readonly featureKeys = computed(() => [...this.keys()].sort());

  /** Whether the signed-in person has `key`. Reactive: read it in a template or computed and it updates on load. */
  has(key: string): boolean {
    this.load();
    return this.keys().has(key);
  }

  /** Whether the person has any of the keys (one key, or a list). Reactive, like has(). */
  hasAny(keys: string | readonly string[]): boolean {
    this.load();
    const set = this.keys();
    return (typeof keys === 'string' ? [keys] : keys).some(k => set.has(k));
  }

  /** Starts the one request per session (idempotent). */
  load(): void {
    if (this.requested) { return; }
    this.requested = true;
    this.http.get<MyFeatureKeys>(this.url).pipe(catchError(() => of<MyFeatureKeys>({ email: null, featureKeys: [] }))).subscribe(r => {
      this.keys.set(new Set(r.featureKeys ?? []));
      this.loadedState.set(true);
      this.resolveReady();
    });
  }
}
